import DocsLayout, { CodeBlock, Callout } from '../../../components/docs/DocsLayout';

export default function DocsApiWebhooks() {
  return (
    <DocsLayout 
      title="Webhooks" 
      description="Real-time event notifications"
    >
      <section className="space-y-6">
        <p className="text-white/70 leading-relaxed">
          Webhooks allow you to receive real-time notifications when events occur in your AIBL agents. Instead of polling the API, your server receives HTTP POST requests when relevant events happen.
        </p>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Setting Up Webhooks</h2>
        
        <ol className="space-y-2 text-white/70">
          <li>1. Navigate to Account Settings in the dashboard</li>
          <li>2. Add your webhook endpoint URL</li>
          <li>3. Select which events to subscribe to</li>
          <li>4. Save and verify the endpoint</li>
        </ol>

        <Callout type="info" title="HTTPS Required">
          Webhook endpoints must use HTTPS. We do not send events to insecure HTTP endpoints.
        </Callout>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Event Types</h2>

        <div className="space-y-4">
          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <h3 className="font-semibold text-white mb-2">transaction.pending</h3>
            <p className="text-sm text-white/60">A new transaction is pending approval (multi-sig only).</p>
          </div>

          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <h3 className="font-semibold text-white mb-2">transaction.confirmed</h3>
            <p className="text-sm text-white/60">A transaction has been confirmed on-chain.</p>
          </div>

          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <h3 className="font-semibold text-white mb-2">transaction.failed</h3>
            <p className="text-sm text-white/60">A transaction failed to execute.</p>
          </div>

          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <h3 className="font-semibold text-white mb-2">agent.low_balance</h3>
            <p className="text-sm text-white/60">Agent wallet balance fell below configured threshold.</p>
          </div>

          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <h3 className="font-semibold text-white mb-2">safe.signature_added</h3>
            <p className="text-sm text-white/60">A co-signer added their signature to a pending Safe transaction.</p>
          </div>

          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <h3 className="font-semibold text-white mb-2">plugin.limit_reached</h3>
            <p className="text-sm text-white/60">Agent reached a configured plugin limit (daily, per-tx).</p>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Webhook Payload</h2>
        
        <p className="text-white/70 mb-4">
          All webhook payloads follow this structure:
        </p>

        <CodeBlock language="json">
{`{
  "id": "evt_abc123xyz",
  "type": "transaction.confirmed",
  "created": 1704067200,
  "data": {
    "agentId": "agent_xyz789",
    "transactionHash": "0x...",
    "chain": "base",
    "operation": "swap",
    "status": "confirmed"
  }
}`}
        </CodeBlock>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Verifying Signatures</h2>
        
        <p className="text-white/70 mb-4">
          Each webhook request includes a signature header for verification:
        </p>

        <CodeBlock language="text">
{`X-AIBL-Signature: sha256=abc123...`}
        </CodeBlock>

        <p className="text-white/70 mb-4 mt-4">
          Verify the signature using your webhook secret:
        </p>

        <CodeBlock language="javascript">
{`const crypto = require('crypto');

function verifyWebhook(payload, signature, secret) {
  const expectedSignature = crypto
    .createHmac('sha256', secret)
    .update(payload)
    .digest('hex');
  
  return \`sha256=\${expectedSignature}\` === signature;
}`}
        </CodeBlock>

        <Callout type="warning" title="Always Verify">
          Always verify webhook signatures before processing events. This prevents attackers from sending fake events to your endpoint.
        </Callout>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Retry Policy</h2>
        
        <p className="text-white/70 mb-4">
          If your endpoint returns a non-2xx status code, we retry with exponential backoff:
        </p>

        <ul className="space-y-2 text-white/70">
          <li>• Retry 1: 1 minute after initial failure</li>
          <li>• Retry 2: 5 minutes after retry 1</li>
          <li>• Retry 3: 30 minutes after retry 2</li>
          <li>• Retry 4: 2 hours after retry 3</li>
          <li>• Retry 5: 24 hours after retry 4 (final)</li>
        </ul>

        <p className="text-white/70 mt-4">
          After 5 failed attempts, the event is marked as failed. You can view failed events in the dashboard and manually retry them.
        </p>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Best Practices</h2>
        
        <ul className="space-y-2 text-white/70">
          <li>• <strong className="text-white">Respond quickly</strong>: Return 200 immediately, then process async</li>
          <li>• <strong className="text-white">Handle duplicates</strong>: Use event ID for idempotency</li>
          <li>• <strong className="text-white">Log everything</strong>: Store raw payloads for debugging</li>
          <li>• <strong className="text-white">Use queues</strong>: Process events through a job queue for reliability</li>
        </ul>
      </section>
    </DocsLayout>
  );
}
